import { FC, useEffect } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router';

import { useAppDispatch } from '@/hooks/redux-hooks';
import {
  setPathnameToStorage,
  useGxTokenFromStorage,
  useTripTokenFromStorage
} from '@/services/storage';
import { getDataHubs } from '@/slices/dataHubSlice';
import { getMarketsList } from '@/slices/marketsListSlice';
import { loginTrip } from '@/slices/tripTokenSlice';
import { getUserConfig } from '@/slices/userConfigSlice';

/**
 * Authentication guard layout that protects routes requiring user authentication.
 *
 * - Checks for GX token presence and redirects to login if missing
 * - Initializes Trip token authentication on mount
 * - Fetches essential user data (markets list, user config) when authenticated
 * - Saves current pathname before redirect for post-login navigation
 */
export const AuthLayout: FC = () => {
  const gxToken = useGxTokenFromStorage();
  const dispatch = useAppDispatch();
  const tripToken = useTripTokenFromStorage();
  const location = useLocation();

  useEffect(() => {
    if (gxToken) {
      dispatch(loginTrip(gxToken));
    }
  }, [dispatch, gxToken]);

  useEffect(() => {
    if (tripToken) {
      dispatch(getMarketsList());
      dispatch(getUserConfig());
      dispatch(getDataHubs());
    }
  }, [dispatch, tripToken]);

  if (!gxToken) {
    setPathnameToStorage(location.pathname);

    return (
      <Navigate
        replace
        to='/login'
      />
    );
  }

  return <Outlet />;
};
